import React, { useState, useEffect } from "react";
import "./Letterprint.css";
import logo from "../../../Assets/jklogo.png";
import logo1 from "../../../Assets/jk.png";
import Sign from "../../../Assets/Uppal_Shah_Stamp.png";
import Sign1 from "../../../Assets/Uppal_Shah_Stamp1.png";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PdfPreview from "../../../Common/PDFPreview";
import PrintButton from "../../../Common/Buttons/PrintPDF";
import HeaderJK from "../../../Assets/HeaderJK.png";
import "../../../Common/Fonts/Signika-Bold-normal";
import "../../../Common/Fonts/Signika-Regular-normal";
import "../../../Common/Fonts/Signika-Medium-normal";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  Chip,
} from "@mui/material";
import AccountMasterHelp from "../../../Helper/AccountMasterHelp";

const API_URL = process.env.REACT_APP_API;

const LetterHeadPrintReport = ({ doc_no, disabledFeild }) => {
  const companyCode = sessionStorage.getItem("Company_Code");
  const [pdfPreview, setPdfPreview] = useState(null);
  const CompanyNameUpdatedDate = sessionStorage.getItem("CompanyNameUpdatedDate");
  const newCompanyName = sessionStorage.getItem("newCompanyName");
  const oldFormerlyName = sessionStorage.getItem("oldFormerlyName");
  const accountingYearRaw = sessionStorage.getItem("Accounting_Year");

  // The Ref No's year segment comes from the active accounting year
  // (set at company/year selection, stored as "<Start_Date> - <End_Date>"),
  // not from the letter's own date.
  const accountingYear = (() => {
    if (!accountingYearRaw) return "";
    const [startDate] = accountingYearRaw.split(" - ");
    const sd = new Date(startDate);
    return Number.isNaN(sd.getTime()) ? "" : sd.getFullYear();
  })();

  // Transfer Letter - the original confirmation letter's party/mill
  // details are reused, but the transfer party, grade, quantity and
  // rate being transferred are deal-specific and collected here.
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferBaseData, setTransferBaseData] = useState(null);
  const [transferGradeDetails, setTransferGradeDetails] = useState([]);
  const [transferListOpen, setTransferListOpen] = useState(false);
  const [transferList, setTransferList] = useState([]);
  const [transferForm, setTransferForm] = useState({
    refNo: "",
    partyCode: "",
    partyAccoid: "",
    partyName: "",
    partyCity: "",
    partyGST: "",
    grade: "",
    quantity: "",
    rate: "",
    season: "",
    note: "",
  });

  // Grade dropdown - sourced from System Master, type 'S' (sugar grades) only
  const [gradeOptions, setGradeOptions] = useState([]);

  useEffect(() => {
    if (!companyCode) return;
    fetch(`${API_URL}/getall-SystemMaster?Company_Code=${companyCode}`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setGradeOptions(list.filter((r) => r.System_Type === "S"));
      })
      .catch(() => setGradeOptions([]));
  }, [companyCode]);

  const handleTransferFieldChange = (field) => (e) => {
    setTransferForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Fired by AccountMasterHelp once the user picks the transfer party -
  // pulls name/city/state/GST straight from the account master instead
  // of being typed in by hand.
  const handleTransferPartySelect = (
    Ac_Code,
    accoid,
    Ac_Name_E,
    Mobile_No,
    Gst_No,
    TDSApplicable,
    GSTStateCode,
    cityname,
    State_Name
  ) => {
    setTransferForm((prev) => ({
      ...prev,
      partyCode: Ac_Code,
      partyAccoid: accoid,
      partyName: Ac_Name_E,
      partyCity: [cityname, State_Name].filter(Boolean).join(", "),
      partyGST: Gst_No,
    }));
  };

  // Entry point for the "Transfer Letter" button: loads the confirmation
  // letter's data, then checks whether any Sugar Transfer Letters already
  // exist for it. If some do, shows a list to pick one to print (or start
  // a new one from there); if none exist yet, goes straight to the blank
  // create form.
  const openTransferDialog = async () => {
    try {
      const response = await fetch(
        `${API_URL}/generating_LetterHead_report?Company_Code=${companyCode}&doc_no=${doc_no}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      const allData = Array.isArray(data.all_data)
        ? data.all_data[0] || {}
        : data.all_data || {};

      let gradeDetails = [];
      try {
        const rawGrades = allData.GradeDetailsJson ?? allData.gradeDetails ?? [];
        gradeDetails = typeof rawGrades === "string" ? JSON.parse(rawGrades) : rawGrades;
        if (!Array.isArray(gradeDetails)) gradeDetails = [];
      } catch (e) {
        gradeDetails = [];
      }

      setTransferBaseData(allData);
      setTransferGradeDetails(gradeDetails);

      let records = [];
      try {
        const existingResp = await fetch(
          `${API_URL}/get-SugarTransferLetter?Company_Code=${companyCode}&Doc_No=${doc_no}`
        );
        if (existingResp.ok) {
          const existingData = await existingResp.json();
          records = Array.isArray(existingData.records) ? existingData.records : [];
        }
      } catch (lookupError) {
        console.error("Error checking for existing transfer letters:", lookupError);
        // fall through, treated the same as "no existing records found"
      }

      if (records.length > 0) {
        setTransferList(records);
        setTransferListOpen(true);
      } else {
        openCreateTransferForm(allData, gradeDetails);
      }
    } catch (error) {
      console.error("Error fetching data for transfer letter:", error);
      window.alert("Unable to load letter data for the transfer letter.");
    }
  };

  // Opens the blank "Sugar Transfer Letter Details" form, pre-filled from
  // the confirmation letter's first grade. Used both when no transfer
  // letter exists yet, and from the "+ Create New" action in the list.
  const openCreateTransferForm = (allData, gradeDetails) => {
    const firstGrade = (gradeDetails && gradeDetails[0]) || {};

    const today = new Date();
    const fyStart = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const fyLabel = String(fyStart).slice(-2) + String(fyStart + 1).slice(-2);
    const autoRefNo = "JK/TL/" + fyLabel + "/" + safe(doc_no);

    setTransferForm({
      refNo: autoRefNo,
      partyCode: "",
      partyAccoid: "",
      partyName: "",
      partyCity: "",
      partyGST: "",
      grade: safe(firstGrade.grade),
      quantity: safe(firstGrade.Quantity),
      rate: safe(firstGrade.Rate),
      season: safe(firstGrade.Season ?? firstGrade.season),
      note: "",
    });
    setTransferListOpen(false);
    setTransferDialogOpen(true);
  };

  // Reprints one previously-saved Sugar Transfer Letter record, picked
  // from the list, without creating a new database row.
  const printExistingTransferLetter = async (record) => {
    setTransferListOpen(false);
    await generateTransferLetterPdf(transferBaseData, {
      refNo: safe(record.Ref_No),
      partyCode: safe(record.Party_Ac_Code),
      partyAccoid: safe(record.Party_Accoid),
      partyName: safe(record.Party_Name),
      partyCity: safe(record.Party_City),
      partyGST: safe(record.Party_GST),
      grade: safe(record.Grade),
      quantity: safe(record.Quantity),
      rate: safe(record.Rate),
      season: safe(record.Season),
      note: safe(record.Note),
    });
  };

  const safe = (value) =>
    value === null || value === undefined ? "" : String(value);

  const fetchData = async (signWithDSC = false) => {
    try {
      const response = await fetch(
        `${API_URL}/generating_LetterHead_report?Company_Code=${companyCode}&doc_no=${doc_no}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      generatePdf(data.all_data, signWithDSC);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Sends the already-generated PDF to the backend to be cryptographically
  // signed with the company's DSC (.p12), which stays server-side only.
  const signPdfWithDSC = async (pdfBlob, box) => {
    try {
      const formData = new FormData();
      formData.append("pdf", pdfBlob, "Letter.pdf");
      if (box) {
        formData.append("box_x1", box.x1);
        formData.append("box_y1", box.y1);
        formData.append("box_x2", box.x2);
        formData.append("box_y2", box.y2);
      }
      const response = await fetch(`${API_URL}/sign-customized-sale-bill-dsc`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "DSC signing failed");
      }
      return await response.blob();
    } catch (error) {
      console.error("DSC signing error:", error);
      alert(`Digital signature failed, showing unsigned PDF instead.\n${error.message}`);
      return pdfBlob;
    }
  };

  // =========================================
  // SUGAR TRANSFER LETTER
  // =========================================

  const generateTransferLetterPdf = async (data, transferInfo) => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const allData = Array.isArray(data) ? data[0] || {} : data || {};

      const W = 210;
      const H = 297;
      const margin = 18;
      const contentWidth = W - margin * 2;

      const BOTTOM_LIMIT = 277;
      const PAGE_TOP = 20;

      const navy = [18, 27, 46];
      const gray = [80, 90, 105];

      const fontList = pdf.getFontList();
      const hasSignika = Object.keys(fontList).some(
        (name) => name.toLowerCase() === "signika-regular"
      );
      const REGULAR_FONT = hasSignika ? "Signika-Regular" : "helvetica";
      const BOLD_FONT = hasSignika ? "Signika-Bold" : "helvetica";

      const setPdfFont = (bold = false) => {
        pdf.setFont(bold ? BOLD_FONT : REGULAR_FONT, "normal");
      };

      const loadImage = (src) =>
        new Promise((resolve, reject) => {
          if (!src) {
            reject(new Error("PDF image source is missing."));
            return;
          }
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error("Unable to load PDF image."));
          img.src = src;
        });

      // Crops transparent / white padding around the signature so it can
      // be drawn big and tight (no hidden blank area inside the PNG).
      const trimImage = (img) => {
        const fallback = { src: img.src, ratio: img.naturalWidth / img.naturalHeight };
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          const ctx = c.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const { data: px, width, height } = ctx.getImageData(0, 0, c.width, c.height);

          let minX = width, minY = height, maxX = -1, maxY = -1;
          for (let yy = 0; yy < height; yy++) {
            for (let xx = 0; xx < width; xx++) {
              const i = (yy * width + xx) * 4;
              const a = px[i + 3];
              const isWhite = px[i] > 240 && px[i + 1] > 240 && px[i + 2] > 240;
              if (a > 20 && !isWhite) {
                if (xx < minX) minX = xx;
                if (xx > maxX) maxX = xx;
                if (yy < minY) minY = yy;
                if (yy > maxY) maxY = yy;
              }
            }
          }
          if (maxX < 0) return fallback;

          const pad = 4;
          minX = Math.max(0, minX - pad);
          minY = Math.max(0, minY - pad);
          maxX = Math.min(width - 1, maxX + pad);
          maxY = Math.min(height - 1, maxY + pad);

          const cw = maxX - minX + 1;
          const ch = maxY - minY + 1;

          const out = document.createElement("canvas");
          out.width = cw;
          out.height = ch;
          out.getContext("2d").drawImage(c, minX, minY, cw, ch, 0, 0, cw, ch);

          return { src: out.toDataURL("image/png"), ratio: cw / ch };
        } catch (e) {
          return fallback;
        }
      };

      const write = (value, x, posY, size = 9, bold = false, color = navy) => {
        setPdfFont(bold);
        pdf.setFontSize(size);
        pdf.setTextColor(...color);
        pdf.text(safe(value), x, posY);
      };

      const paragraph = (value, x, posY, width = contentWidth, size = 9, lineHeight = 5) => {
        setPdfFont(false);
        pdf.setFontSize(size);
        pdf.setTextColor(...navy);

        const lines = pdf.splitTextToSize(safe(value), width);
        let cursorY = posY;

        lines.forEach((line) => {
          if (cursorY + lineHeight > BOTTOM_LIMIT) {
            pdf.addPage();
            cursorY = PAGE_TOP;
            setPdfFont(false);
            pdf.setFontSize(size);
            pdf.setTextColor(...navy);
          }
          pdf.text(line, x, cursorY);
          cursorY += lineHeight;
        });

        return cursorY;
      };

      const formatAmount = (value) =>
        (Number(value) || 0).toLocaleString("en-IN", {
          maximumFractionDigits: 2,
        });

      // Paragraph built from { text, bold } segments, so specific phrases
      // (quantity, rate, party name) can be bold while the rest stays regular.
      const richParagraph = (segments, x, posY, width = contentWidth, size = 9, lineHeight = 5) => {
        pdf.setFontSize(size);
        pdf.setTextColor(...navy);

        const tokens = [];
        segments.forEach((seg) => {
          String(seg.text)
            .split(/(\s+)/)
            .filter((w) => w !== "")
            .forEach((w) => {
              if (/^\s+$/.test(w)) {
                if (tokens.length) tokens[tokens.length - 1].text += w;
              } else {
                tokens.push({ text: w, bold: !!seg.bold });
              }
            });
        });

        const lines = [[]];
        let lineWidth = 0;
        tokens.forEach((tok) => {
          setPdfFont(tok.bold);
          const w = pdf.getTextWidth(tok.text);
          if (lineWidth + w > width && lines[lines.length - 1].length > 0) {
            lines.push([]);
            lineWidth = 0;
          }
          lines[lines.length - 1].push(tok);
          lineWidth += w;
        });

        let cursorY = posY;
        lines.forEach((lineTokens) => {
          if (cursorY + lineHeight > BOTTOM_LIMIT) {
            pdf.addPage();
            cursorY = PAGE_TOP;
          }
          let cx = x;
          lineTokens.forEach((tok) => {
            setPdfFont(tok.bold);
            pdf.text(tok.text, cx, cursorY);
            cx += pdf.getTextWidth(tok.text);
          });
          cursorY += lineHeight;
        });

        return cursorY;
      };

      // =========================================
      // COMPANY TEMPLATE (same rule as the trade confirmation letter)
      // =========================================

      const docDate = new Date(allData.Letter_Date);
      const cnameUpdatedDate = new Date(CompanyNameUpdatedDate);

      const isOldTemplate =
        !Number.isNaN(docDate.getTime()) &&
        !Number.isNaN(cnameUpdatedDate.getTime()) &&
        docDate < cnameUpdatedDate;

      const displayCompanyName = safe(
        isOldTemplate ? newCompanyName : allData.Company_Name_E
      );

      const logoToUse = isOldTemplate ? logo : logo1;
      const signToUse = isOldTemplate ? Sign1 : Sign;
      const formerlyName = isOldTemplate ? oldFormerlyName : allData.AL1;

      const sellerName = safe(allData.AC_NAME);

      const ordinalDate = (d) => {
        const day = d.getDate();
        const suffix =
          day % 10 === 1 && day !== 11
            ? "st"
            : day % 10 === 2 && day !== 12
            ? "nd"
            : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";
        const month = d.toLocaleDateString("en-GB", { month: "long" });
        return day + suffix + " " + month + ", " + d.getFullYear();
      };

      const today = new Date();
      const trfDate = ordinalDate(today);

      const ourRefNo = "JK/TC/" + accountingYear + "/" + safe(allData.Letter_No);

      // =========================================
      // FOOTER
      // =========================================

      const footer = (pageNo, totalPages) => {
        pdf.setDrawColor(225, 229, 235);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 282, W - margin, 282);

        write(displayCompanyName + " | Sugar Transfer Letter", margin, 287, 7, false, gray);

        setPdfFont(false);
        pdf.setFontSize(7);
        pdf.setTextColor(...gray);
        pdf.text("Page " + pageNo + " of " + totalPages, W - margin, 287, { align: "right" });
      };

      // =========================================
      // PAGE 1 - HEADER
      // =========================================

      if (!isOldTemplate) {
        const headerImg = await loadImage(HeaderJK);
        pdf.addImage(headerImg, "PNG", 0, 5, 210, 35);
      } else {
        const logoImg = await loadImage(logoToUse);
        pdf.addImage(logoImg, "PNG", 10, 7, 30, 30);

        setPdfFont(true);
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text(displayCompanyName, 45, 12);

        setPdfFont(false);
        pdf.setFontSize(9);
        pdf.text(safe(formerlyName), 45, 17);
        pdf.text(safe(allData.AL2), 45, 21);
        pdf.text(safe(allData.AL3), 45, 25);
        pdf.text(safe(allData.AL4), 45, 29);
        pdf.text(safe(allData.Other), 45, 33);
        pdf.text(safe(allData.BillFooter), 45, 37);
      }

      pdf.setDrawColor(...navy);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 42, W - margin, 42);

      // =========================================
      // REF NO AND DATE (plain text, no background box)
      // =========================================

      write("Ref. No. : " + safe(transferInfo.refNo), margin, 51, 8.5, false);

      setPdfFont(true);
      pdf.setFontSize(8.5);
      pdf.setTextColor(...navy);
      pdf.text("Date: " + trfDate, W - margin, 51, { align: "right" });

      // =========================================
      // PARTY DETAILS
      // =========================================

      let y = 64;

      write("To,", margin, y, 9, true);
      y += 5;

      write("The Managing Director & Chairman,", margin, y, 9, true);
      y += 5;

      write(
        sellerName + (allData.gov_millcode ? " (" + allData.gov_millcode + ")" : "") + ",",
        margin,
        y,
        9
      );
      y += 5;

      if (allData.ADDRESS) {
        y = paragraph(allData.ADDRESS, margin, y, contentWidth, 9, 4.8);
      }

      const locationText =
        safe(allData.CITY) +
        (allData.PINCODE ? " - " + safe(allData.PINCODE) : "") +
        (allData.District ? ", Dist: " + safe(allData.District) : "") +
        (allData.State ? ", " + safe(allData.State) : "");

      if (locationText.trim()) {
        y = paragraph(locationText, margin, y, contentWidth, 9, 4.8);
      }

      y += 7;

      setPdfFont(true);
      pdf.setFontSize(9);
      pdf.setTextColor(...navy);
      pdf.text(
        "Subject: Sugar Transfer Letter for " +
          formatAmount(transferInfo.quantity) +
          " qntls.",
        W / 2,
        y,
        { align: "center" }
      );
      y += 8;

      // =========================================
      // BODY
      // =========================================

      write("Dear Sir,", margin, y, 9);
      y += 9;

      const docDateText = Number.isNaN(docDate.getTime()) ? "" : ordinalDate(docDate);

      const para1Segments = [
        {
          text:
            "As per our sugar purchase trade confirmation letter, Ref. no. " +
            ourRefNo +
            (docDateText ? " dated " + docDateText : "") +
            ", we have bought " +
            formatAmount(totalQuantityForTransfer(allData)) +
            " Quintel sugar, out of which please transfer ",
        },
        { text: formatAmount(transferInfo.quantity) + " Qntls.", bold: true },
        {
          text:
            ", " +
            safe(transferInfo.grade) +
            " grade" +
            (transferInfo.season ? ", " + safe(transferInfo.season) + " season" : "") +
            ", Rate ",
        },
        { text: "Rs. " + formatAmount(transferInfo.rate) + ".00", bold: true },
        { text: " per Quintal, to " },
        { text: safe(transferInfo.partyName) + ", " + safe(transferInfo.partyCity), bold: true },
        { text: " wide GST No. " + safe(transferInfo.partyGST) + "." },
      ];

      y = richParagraph(para1Segments, margin, y, contentWidth, 9, 5);
      y += 5;

      const para2 =
        "Please note the payment for the " +
        formatAmount(transferInfo.quantity) +
        " Qntl., " +
        safe(transferInfo.grade) +
        " grade, at Rs. " +
        formatAmount(transferInfo.rate) +
        ".00 + GST will be done by the " +
        safe(transferInfo.partyName) +
        ", " +
        safe(transferInfo.partyCity) +
        ". The terms and conditions for payment and lifting will be the " +
        "same as mentioned in the confirmation letter.";

      y = paragraph(para2, margin, y, contentWidth, 9, 5);
      y += 6;

      if (safe(transferInfo.note).trim()) {
        y = richParagraph(
          [{ text: "Note: ", bold: true }, { text: safe(transferInfo.note) }],
          margin,
          y,
          contentWidth,
          9,
          5
        );
        y += 6;
      }

      write("Kindly acknowledge and confirm the same.", margin, y, 9);
      y += 6;

      write("Thanking you,", margin, y, 9);
      y += 14;

      // =========================================
      // SIGNATURE SECTION (dual - JK side has the Director sign)
      // =========================================

      const leftX = margin;

      const SIGN_MAX_W = 60;
      const SIGN_MAX_H = 38;

      const rawSign = await loadImage(signToUse);
      const signImgData = trimImage(rawSign);

      let signHeight = SIGN_MAX_H;
      let signWidth = signHeight * signImgData.ratio;
      if (signWidth > SIGN_MAX_W) {
        signWidth = SIGN_MAX_W;
        signHeight = signWidth / signImgData.ratio;
      }

      const signY = y + 10;
      const labelY = signY + signHeight + 5;

      if (labelY + 11 > BOTTOM_LIMIT) {
        pdf.addPage();
        y = PAGE_TOP;
      }

      write("For. " + displayCompanyName, leftX, y, 8.5, true);

      pdf.addImage(signImgData.src, "PNG", leftX, signY, signWidth, signHeight);

      write("Authorized Signatory", leftX, labelY, 8, true);

      // =========================================
      // FOOTERS / OUTPUT
      // =========================================

      const totalPages = pdf.internal.getNumberOfPages();
      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        footer(page, totalPages);
      }
      pdf.setPage(totalPages);

      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setPdfPreview({
        url: pdfUrl,
        data: {
          ...allData,
          LetterNo: safe(allData.Letter_No),
          CompanyName: displayCompanyName,
        },
      });
    } catch (error) {
      console.error("Transfer Letter PDF Error:", error);
      window.alert(
        "PDF Generation Error: " + (error.message || "Unable to generate transfer letter.")
      );
    }
  };

  // Same "total quantity in the original deal" calc the main letter uses,
  // kept local to the transfer letter so it doesn't touch generatePdf.
  const totalQuantityForTransfer = (allData) => {
    let gradeDetails = [];
    try {
      const rawGrades = allData.GradeDetailsJson ?? allData.gradeDetails ?? [];
      gradeDetails = typeof rawGrades === "string" ? JSON.parse(rawGrades) : rawGrades;
      if (!Array.isArray(gradeDetails)) gradeDetails = [];
    } catch (e) {
      gradeDetails = [];
    }
    return gradeDetails.reduce((sum, item) => sum + (Number(item.Quantity) || 0), 0);
  };

  const generatePdf = async (data, signWithDSC = false) => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const allData = Array.isArray(data) ? data[0] || {} : data || {};

      const W = 210;
      const H = 297;
      const margin = 18;
      const contentWidth = W - margin * 2;

      const BOTTOM_LIMIT = 277;
      const PAGE_TOP = 20;

      const navy = [18, 27, 46];
      const gray = [80, 90, 105];
      const lightGray = [247, 248, 250];

      const fontList = pdf.getFontList();
      const hasSignika = Object.keys(fontList).some(
        (name) => name.toLowerCase() === "signika-regular"
      );

      const REGULAR_FONT = hasSignika ? "Signika-Regular" : "helvetica";
      const BOLD_FONT = hasSignika ? "Signika-Bold" : "helvetica";
      const PDF_FONT = REGULAR_FONT;

      const setPdfFont = (bold = false) => {
        pdf.setFont(bold ? BOLD_FONT : REGULAR_FONT, "normal");
      };

      // =========================================
      // COMMON HELPERS
      // =========================================

      const safe = (value) =>
        value === null || value === undefined ? "" : String(value);

      const dateText = (value) => {
        if (!value) return "";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return safe(value);

        const day = d.getUTCDate();
        const suffix =
          day % 10 === 1 && day !== 11
            ? "st"
            : day % 10 === 2 && day !== 12
            ? "nd"
            : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";
        const month = d.toLocaleDateString("en-GB", {
          month: "long",
          timeZone: "UTC",
        });
        return day + suffix + " " + month + ", " + d.getUTCFullYear();
      };

      const formatAmount = (value) =>
        (Number(value) || 0).toLocaleString("en-IN", {
          maximumFractionDigits: 2,
        });

      const loadImage = (src) =>
        new Promise((resolve, reject) => {
          if (!src) {
            reject(new Error("PDF image source is missing."));
            return;
          }
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error("Unable to load PDF image."));
          img.src = src;
        });

      // Crops transparent / white padding around the signature so it can be
      // drawn big and tight (no hidden blank area inside the PNG).
      const trimImage = (img) => {
        const fallback = {
          src: img.src,
          ratio: img.naturalWidth / img.naturalHeight,
        };
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          const ctx = c.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const { data: px, width, height } = ctx.getImageData(
            0,
            0,
            c.width,
            c.height
          );

          let minX = width,
            minY = height,
            maxX = -1,
            maxY = -1;

          for (let yy = 0; yy < height; yy++) {
            for (let xx = 0; xx < width; xx++) {
              const i = (yy * width + xx) * 4;
              const a = px[i + 3];
              const isWhite = px[i] > 240 && px[i + 1] > 240 && px[i + 2] > 240;
              if (a > 20 && !isWhite) {
                if (xx < minX) minX = xx;
                if (xx > maxX) maxX = xx;
                if (yy < minY) minY = yy;
                if (yy > maxY) maxY = yy;
              }
            }
          }

          if (maxX < 0) return fallback;

          const pad = 4;
          minX = Math.max(0, minX - pad);
          minY = Math.max(0, minY - pad);
          maxX = Math.min(width - 1, maxX + pad);
          maxY = Math.min(height - 1, maxY + pad);

          const cw = maxX - minX + 1;
          const ch = maxY - minY + 1;

          const out = document.createElement("canvas");
          out.width = cw;
          out.height = ch;
          out.getContext("2d").drawImage(c, minX, minY, cw, ch, 0, 0, cw, ch);

          return { src: out.toDataURL("image/png"), ratio: cw / ch };
        } catch (e) {
          return fallback;
        }
      };

      const write = (value, x, posY, size = 9, bold = false, color = navy) => {
        setPdfFont(bold);
        pdf.setFontSize(size);
        pdf.setTextColor(...color);
        pdf.text(safe(value), x, posY);
      };

      // Draws one line justified (both edges flush) by spreading the extra
      // space evenly between words.
      const drawJustifiedLine = (line, x, posY, width) => {
        const words = line.trim().split(/\s+/);
        if (words.length < 2) {
          pdf.text(line, x, posY);
          return;
        }
        const wordsWidth = words.reduce((s, w) => s + pdf.getTextWidth(w), 0);
        const gap = (width - wordsWidth) / (words.length - 1);
        let cx = x;
        words.forEach((w) => {
          pdf.text(w, cx, posY);
          cx += pdf.getTextWidth(w) + gap;
        });
      };

      // Draws line by line, breaking to a fresh page exactly where the
      // text runs out of room. With justify=true every line except the
      // last one of the paragraph ends at the same right edge.
      const paragraph = (
        value,
        x,
        posY,
        width = contentWidth,
        size = 8.5,
        lineHeight = 4.4,
        justify = false
      ) => {
        setPdfFont(false);
        pdf.setFontSize(size);
        pdf.setTextColor(...navy);

        const lines = pdf.splitTextToSize(safe(value), width);
        let cursorY = posY;

        lines.forEach((line, idx) => {
          if (cursorY + lineHeight > BOTTOM_LIMIT) {
            pdf.addPage();
            cursorY = PAGE_TOP;
            setPdfFont(false);
            pdf.setFontSize(size);
            pdf.setTextColor(...navy);
          }

          const isLast = idx === lines.length - 1;
          if (justify && !isLast) {
            drawJustifiedLine(line, x, cursorY, width);
          } else {
            pdf.text(line, x, cursorY);
          }
          cursorY += lineHeight;
        });

        return cursorY;
      };

      const heading = (value, posY) => {
        write(value, margin, posY, 9, true);
        return posY + 4;
      };

      // =========================================
      // MULTIPLE GRADE DETAILS
      // =========================================

      let gradeDetails = [];

      try {
        const rawGrades = allData.GradeDetailsJson ?? allData.gradeDetails ?? [];
        gradeDetails =
          typeof rawGrades === "string" ? JSON.parse(rawGrades) : rawGrades;
        if (!Array.isArray(gradeDetails)) gradeDetails = [];
      } catch (error) {
        console.error("Grade JSON parsing error:", error);
        gradeDetails = [];
      }

      if (gradeDetails.length === 0) {
        gradeDetails = [
          {
            grade: allData.grade ?? "",
            Quantity: allData.Quantity ?? "",
            Rate: allData.Rate ?? "",
            Advance: allData.Advance ?? "",
          },
        ];
      }

      const totalQuantity = gradeDetails.reduce(
        (sum, item) => sum + (Number(item.Quantity) || 0),
        0
      );

      // =========================================
      // COMPANY TEMPLATE
      // =========================================

      const docDate = new Date(allData.Letter_Date);
      const cnameUpdatedDate = new Date(CompanyNameUpdatedDate);

      const isOldTemplate =
        !Number.isNaN(docDate.getTime()) &&
        !Number.isNaN(cnameUpdatedDate.getTime()) &&
        docDate < cnameUpdatedDate;

      const displayCompanyName = safe(
        isOldTemplate ? newCompanyName : allData.Company_Name_E
      );

      const logoToUse = isOldTemplate ? logo : logo1;
      const signToUse = isOldTemplate ? Sign1 : Sign;

      const formerlyName = isOldTemplate ? oldFormerlyName : allData.AL1;

      const sellerName = safe(allData.AC_NAME);
      const letterDate = dateText(allData.Letter_Date);

      const refNo = "JK/TC/" + accountingYear + "/" + safe(allData.Letter_No);

      const buyerName = displayCompanyName;

      // =========================================
      // FOOTER
      // =========================================

      const footer = (pageNo, totalPages) => {
        pdf.setDrawColor(225, 229, 235);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 282, W - margin, 282);

        write(
          displayCompanyName + " | Trade Confirmation Agreement",
          margin,
          287,
          7,
          false,
          gray
        );

        setPdfFont(false);
        pdf.setFontSize(7);
        pdf.setTextColor(...gray);
        pdf.text("Page " + pageNo + " of " + totalPages, W - margin, 287, {
          align: "right",
        });
      };

      // =========================================
      // PAGE 1 - HEADER
      // =========================================

      if (!isOldTemplate) {
        const headerImg = await loadImage(HeaderJK);
        pdf.addImage(headerImg, "PNG", 0, 5, 210, 35);
      } else {
        const logoImg = await loadImage(logoToUse);
        pdf.addImage(logoImg, "PNG", 10, 7, 30, 30);

        setPdfFont(true);
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text(displayCompanyName, 45, 12);

        setPdfFont(false);
        pdf.setFontSize(9);

        pdf.text(safe(formerlyName), 45, 17);
        pdf.text(safe(allData.AL2), 45, 21);
        pdf.text(safe(allData.AL3), 45, 25);
        pdf.text(safe(allData.AL4), 45, 29);
        pdf.text(safe(allData.Other), 45, 33);
        pdf.text(safe(allData.BillFooter), 45, 37);
      }

      pdf.setDrawColor(...navy);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 42, W - margin, 42);

      // =========================================
      // REFERENCE NUMBER AND DATE (plain text, no background box)
      // =========================================

      const boxY = 47;
      const boxH = 9;
      const boxPad = 0;
      const boxTextY = boxY + boxH / 2 + 1.2;

      write("Ref No: " + refNo, margin + boxPad, boxTextY, 8.5, false);

      setPdfFont(true);
      pdf.setFontSize(8.5);
      pdf.setTextColor(...navy);
      pdf.text("Date: " + letterDate, W - margin - boxPad, boxTextY, {
        align: "right",
      });

      // =========================================
      // PARTY DETAILS
      // =========================================

      let y = 64;

      write("To,", margin, y, 9, true);
      y += 5;

      write("The Managing Director & Chairman", margin, y, 9, true);
      y += 5;

      write(
        sellerName + (allData.gov_millcode ? " (" + allData.gov_millcode + ")" : ""),
        margin,
        y,
        9
      );
      y += 5;

      if (allData.ADDRESS) {
        y = paragraph(allData.ADDRESS, margin, y, contentWidth, 9, 4.8);
      }

      if (allData.GST_no) {
        y = paragraph("GST No: " + safe(allData.GST_no), margin, y, contentWidth, 9, 4.8);
      }

      const locationText = [
        allData.CITY,
        allData.PINCODE,
        allData.District,
        allData.State,
      ]
        .filter(Boolean)
        .join(" - ");

      if (locationText) {
        y = paragraph(locationText, margin, y + 1, contentWidth, 9, 4.8);
      }

      y += 7;

      setPdfFont(true);
      pdf.setFontSize(9);
      pdf.setTextColor(...navy);
      pdf.text(
        "Subject: " +
          safe(allData.Subject || "Formal Confirmation of Sugar Purchase / Sale Agreement"),
        W / 2,
        y,
        { align: "center" }
      );
      y += 8;

      // =========================================
      // INTRODUCTION
      // =========================================

      write("Dear Sir,", margin, y, 9);
      y += 7;

      const reference =
        "This is with reference to the sugar sale concluded by your " +
        "goodself on " +
        letterDate +
        " for " +
        formatAmount(totalQuantity) +
        " Qntls.";

      y = paragraph(reference, margin, y, contentWidth, 9, 5, true);
      y += 4;

      const confirmationIntro =
        "As per the communication with you, we hereby confirm, record " +
        "that the following sugar has been allotted/sold to us by you:";

      y = paragraph(confirmationIntro, margin, y, contentWidth, 9, 5, true);
      y += 6;

      // =========================================
      // DYNAMIC GRADE TABLE (numbered grades: 1. 2. 3. ...)
      // =========================================

      const gradeCount = gradeDetails.length;

      const labelWidth = 35;
      const qtyWidth = 26;
      const seasonWidth = 22;
      const advanceWidth = 26;
      const rateWidth = contentWidth - labelWidth - qtyWidth - seasonWidth - advanceWidth;

      const columnStyles = {
        0: { cellWidth: labelWidth, font: BOLD_FONT, fontStyle: "normal", halign: "left" },
        1: { cellWidth: qtyWidth, halign: "left" },
        2: { cellWidth: rateWidth, halign: "left" },
        3: { cellWidth: seasonWidth, halign: "left" },
        4: { cellWidth: advanceWidth, halign: "left" },
      };

      const fullRow = (label, value) => [
        {
          content: safe(label),
          styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" },
        },
        {
          content: safe(value),
          colSpan: 4,
          styles: { halign: "left", fontStyle: "normal" },
        },
      ];

      const formatRate = (value) =>
        "₹" +
        (Number(value) || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) +
        " + GST per quintal";

      const gradeHeaderRow = [
        { content: "Grade", styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" } },
        { content: "Quantity", styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" } },
        { content: "Rate", styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" } },
        { content: "Season", styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" } },
        { content: "Advance", styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" } },
      ];

      const gradeRows = gradeDetails.map((item, idx) => [
        {
          content: idx + 1 + ". " + safe(item.grade),
          styles: { font: REGULAR_FONT, fontStyle: "normal", halign: "left" },
        },
        {
          content: formatAmount(item.Quantity) + " Quintal",
          styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" },
        },
        {
          content: formatRate(item.Rate),
          styles: { font: BOLD_FONT, fontStyle: "normal", halign: "left" },
        },
        {
          content: safe(item.Season),
          styles: { font: REGULAR_FONT, fontStyle: "normal", halign: "left" },
        },
        {
          content: Number(item.Advance) > 0 ? "₹" + formatAmount(item.Advance) : "-",
          styles: { font: REGULAR_FONT, fontStyle: "normal", halign: "left" },
        },
      ]);

      const specifications = [
        fullRow("Buyer", buyerName + ", Kolhapur"),
        fullRow("Seller", sellerName),
        ...(allData.Mill2_Name
          ? [
              fullRow(
                "Mill",
                safe(allData.Mill2_Name) +
                  (allData.Mill2_GovCode ? " (Gov Code: " + safe(allData.Mill2_GovCode) + ")" : "")
              ),
            ]
          : []),
        fullRow("Commodity", safe(allData.Commodity)),
        fullRow("Quality", safe(allData.Specification)),
        gradeHeaderRow,
        ...gradeRows,
        fullRow("Delivery", safe(allData.Delivery_terms)),
        ...(allData.PaymentDue_Date
          ? [fullRow("Payment", "On or before " + dateText(allData.PaymentDue_Date))]
          : []),
        ...(allData.Lifting_Date
          ? [fullRow("Lifting", "Up to " + dateText(allData.Lifting_Date))]
          : []),
        ...(safe(allData.Note).trim() ? [fullRow("Note", safe(allData.Note))] : []),
      ];

      if (typeof pdf.autoTable !== "function") {
        throw new Error("jsPDF AutoTable plugin is not loaded.");
      }

      pdf.autoTable({
        startY: y,
        margin: { left: margin, right: margin, top: 20, bottom: 20 },
        tableWidth: contentWidth,
        theme: "plain",
        body: specifications,
        rowPageBreak: "avoid",
        styles: {
          font: PDF_FONT,
          fontStyle: "normal",
          fontSize: gradeCount > 4 ? 7 : 8.5,
          textColor: navy,
          halign: "left",
          valign: "middle",
          cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
          lineWidth: 0,
          overflow: "linebreak",
        },
        columnStyles,
        alternateRowStyles: { fillColor: lightGray },
        didDrawCell: (cellData) => {
          if (cellData.section !== "body") return;
          pdf.setDrawColor(229, 232, 237);
          pdf.setLineWidth(0.1);
          pdf.line(
            cellData.cell.x,
            cellData.cell.y + cellData.cell.height,
            cellData.cell.x + cellData.cell.width,
            cellData.cell.y + cellData.cell.height
          );
        },
      });

      if (pdf.internal.getNumberOfPages() !== 1) {
        throw new Error(
          "Commercial specifications exceed Page 1. Reduce spacing or font size."
        );
      }

      // =========================================
      // TERMS AND CONDITIONS (justified, tighter spacing)
      // =========================================

      const clauseGap = 3;
      const subParaGap = 2;

      const ensureSpace = (neededHeight) => {
        if (y + neededHeight > BOTTOM_LIMIT) {
          pdf.addPage();
          y = PAGE_TOP;
        }
      };

      const drawClauseMulti = (headingText, paragraphs, options = {}) => {
        const innerGap = options.innerGap ?? clauseGap;
        const trailingGap = options.trailingGap ?? clauseGap;

        ensureSpace(4 + 4.4);
        y = heading(headingText, y);

        paragraphs.forEach((p, idx) => {
          y = paragraph(p, margin, y, contentWidth, 8.5, 4.4, true);
          y += idx === paragraphs.length - 1 ? trailingGap : innerGap;
        });
      };

      const drawClause = (headingText, bodyText) =>
        drawClauseMulti(headingText, [bodyText]);

      y = pdf.lastAutoTable.finalY + 6;

      ensureSpace(4 + 4);
      y = heading("Terms and Condition:", y);

      drawClause(
        "1. Transferability",
        "The contract is transferable in full or in part to other " +
          "parties, at the buyer's option. The parties have no " +
          "relationship other than that of buyer and seller."
      );

      drawClause(
        "2. Dispute Resolution",
        "All disputes, differences or claims arising out of or in " +
          "connection with this Contract, including its interpretation, " +
          "performance, breach or termination, shall be referred to and " +
          "finally resolved by arbitration administered under the Rules " +
          "of Arbitration of the Indian Merchants' Chamber (IMC), Mumbai. " +
          "The arbitration shall be conducted by a sole arbitrator, or, " +
          "if the IMC so determines, by a panel of three arbitrators, " +
          "appointed in accordance with the said Rules. The seat of " +
          "arbitration shall be Kolhapur, and the venue of hearings shall " +
          "be Mumbai or Kolhapur as the arbitral tribunal may decide. The " +
          "proceedings shall be conducted in English. The award shall be " +
          "final and binding on the parties and enforceable under the " +
          "Arbitration and Conciliation Act, 1996, as amended. Subject to " +
          "the above, the courts at Kolhapur alone shall have exclusive " +
          "jurisdiction in all matters arising out of this Contract."
      );

      drawClause(
        "3. Change in Government Policy or Regulation",
        "If, after the date of this Contract, the Government of India " +
          "or any State Government or authority revises the Minimum " +
          "Selling Price (MSP) or any other prescribed price of sugar, or " +
          "issues any new notification, order, guideline or regulation " +
          "affecting sugar prices, stock limits, release quota, movement " +
          "or lifting conditions, then the option to execute, modify, " +
          "defer or cancel this trade, in full or in part, shall rest " +
          "solely with JK India eAgriTech Limited, Kolhapur " +
          "(eBuySugar.com). Its decision shall be final and binding on " +
          "the seller. In case of cancellation of any unexecuted " +
          "quantity, any amount paid by the Buyer against such quantity " +
          "shall be refunded without interest, and neither party shall " +
          "have any further claim against the other in that respect. " +
          "This clause is subject to the exclusive jurisdiction of the " +
          "courts at Kolhapur."
      );

      drawClause(
        "4. Entire Agreement",
        "All previous communications in respect of this transaction " +
          "are hereby superseded, and this letter constitutes the entire " +
          "agreement between the parties."
      );

      drawClauseMulti(
        "5. Confirmation and Deemed Acceptance",
        [
          "This Contract is being sent to you through email and/or " +
            "WhatsApp on the registered email ID and mobile number " +
            "available in your eBuySugar.com account. The time of " +
            "dispatch recorded in the system of JK India eAgriTech " +
            "Limited shall be treated as the time of receipt.",

          "Upon receipt, this Contract shall be deemed to have been " +
            "accepted and confirmed by you, unless you communicate a " +
            "written denial, objection or discrepancy by email to " +
            "trade@ebuysugar.com within 12 (twelve) hours of its " +
            "receipt. Any objection received after this period, or " +
            "through any other mode, shall not be entertained.",

          "Upon expiry of the said 12 hours without a written denial, " +
            "this Contract shall become final, conclusive and binding " +
            "on you. JK India eAgriTech Limited shall then proceed " +
            "with its commercial and financial arrangements on the " +
            "basis that the above particulars correctly record the " +
            "allotment/transaction and that no dispute has been raised " +
            "by you. Such non-objection, together with the tender " +
            "records, allotment records, correspondence and subsequent " +
            "conduct of the parties, shall be relied upon by JK India " +
            "eAgriTech Limited as evidence of the confirmed transaction.",

          "Kindly acknowledge and confirm.",
        ],
        { innerGap: subParaGap, trailingGap: 5 }
      );

      // =========================================
      // SIGNATURE SECTION (tight, bigger sign, no gap)
      // =========================================

      const leftX = margin;

      // Signature box: trimmed image, scaled to fit while keeping ratio.
      const SIGN_MAX_W = 60;
      const SIGN_MAX_H = 38;

      let signImgData = null;
      let signWidth = 0;
      let signHeight = 14; // DSC box height when using digital signature

      if (!signWithDSC) {
        const rawSign = await loadImage(signToUse);
        signImgData = trimImage(rawSign);

        signHeight = SIGN_MAX_H;
        signWidth = signHeight * signImgData.ratio;

        if (signWidth > SIGN_MAX_W) {
          signWidth = SIGN_MAX_W;
          signHeight = signWidth / signImgData.ratio;
        }
      }

      // "For ..." line + gap + signature + gap + Director/Date/Place
      ensureSpace(3 + signHeight + 5 + 11 + 4);

      const signY = y + 3;
      const labelY = signY + signHeight + 5;

      write("For " + displayCompanyName, leftX, y, 8.5, true);

      let dscBox = null;

      if (signWithDSC) {
        const ptPerMm = 95 / 25.4;

        dscBox = {
          x1: leftX * ptPerMm,
          x2: (leftX + 65) * ptPerMm,
          y1: (H - (signY + signHeight)) * ptPerMm,
          y2: (H - signY) * ptPerMm,
        };
      } else {
        pdf.addImage(
          signImgData.src,
          "PNG",
          leftX,
          signY,
          signWidth,
          signHeight
        );
      }

      write("Authorised Signatory", leftX, labelY, 8.5, true);

      // =========================================
      // FOOTERS
      // =========================================

      const totalPages = pdf.internal.getNumberOfPages();

      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        footer(page, totalPages);
      }

      pdf.setPage(totalPages);

      // =========================================
      // GENERATE / SIGN / PREVIEW
      // =========================================

      const pdfBlob = pdf.output("blob");

      const finalBlob = signWithDSC
        ? await signPdfWithDSC(pdfBlob, dscBox)
        : pdfBlob;

      const pdfUrl = URL.createObjectURL(finalBlob);

      setPdfPreview({
        url: pdfUrl,
        data: {
          ...allData,
          LetterNo: safe(allData.Letter_No),
          CompanyName: displayCompanyName,
        },
      });
    } catch (error) {
      console.error("Trade Confirmation PDF Error:", error);
      window.alert(
        "PDF Generation Error: " + (error.message || "Unable to generate PDF.")
      );
    }
  };

  const label = "LetterHead";

  return (
    <div id="pdf-content">
      {pdfPreview && (
        <PdfPreview
          pdfData={pdfPreview.url}
          apiData={pdfPreview.data}
          label={label}
        />
      )}
      <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
        <PrintButton
          disabledFeild={disabledFeild}
          fetchData={fetchData}
          label={"LetterHead"}
        />
        {/* <PrintButton disabledFeild={disabledFeild} fetchData={() => fetchData(true)} label={"LetterHead"} /> */}
        <PrintButton
          disabledFeild={disabledFeild}
          fetchData={openTransferDialog}
          label={"Transfer Letter"}
        />
      </div>

      <Dialog
        open={transferListOpen}
        onClose={() => setTransferListOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sugar Transfer Letters for Doc No {doc_no}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
            {transferList.length} transfer letter{transferList.length > 1 ? "s" : ""} already created
            for this confirmation letter. Pick one to print, or create another.
          </Typography>
          <List sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
            {transferList.map((record, idx) => (
              <React.Fragment key={record.id}>
                {idx > 0 && <Divider />}
                <ListItemButton onClick={() => printExistingTransferLetter(record)}>
                  <ListItemText
                    primary={
                      safe(record.Ref_No) + " — " + safe(record.Party_Name)
                    }
                    secondary={
                      [
                        record.Grade && "Grade: " + record.Grade,
                        record.Quantity && record.Quantity + " Qtl",
                        record.Rate && "₹" + record.Rate + "/Qtl",
                        record.Season,
                      ]
                        .filter(Boolean)
                        .join(" • ")
                    }
                  />
                  <Chip size="small" label="Print" color="primary" variant="outlined" />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferListOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => openCreateTransferForm(transferBaseData, transferGradeDetails)}
          >
            + Create New
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sugar Transfer Letter Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ref. No."
              value={transferForm.refNo}
              onChange={handleTransferFieldChange("refNo")}
              fullWidth
              size="small"
            />
            <div>
              <label
                style={{ fontSize: "12px", fontWeight: 600, color: "#555" }}
              >
                Transfer To (Party)
              </label>
              <AccountMasterHelp
                name="TRANSFER_PARTY_CODE"
                onAcCodeClick={handleTransferPartySelect}
                CategoryName={transferForm.partyName}
                CategoryCode={transferForm.partyCode}
                tabIndexHelp={1}
                Ac_type={["P", "M"]}
              />
            </div>
            <TextField
              label="Transfer Party City / State"
              value={transferForm.partyCity}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Transfer Party GST No."
              value={transferForm.partyGST}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <TextField
              select
              label="Grade"
              value={transferForm.grade}
              onChange={handleTransferFieldChange("grade")}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
            >
              <option value=""></option>
              {gradeOptions.map((g) => (
                <option key={g.systemid} value={g.System_Name_E}>
                  {g.System_Name_E}
                </option>
              ))}
            </TextField>
            <TextField
              label="Quantity (Quintal)"
              value={transferForm.quantity}
              onChange={handleTransferFieldChange("quantity")}
              fullWidth
              size="small"
              type="number"
            />
            <TextField
              label="Rate (per quintal)"
              value={transferForm.rate}
              onChange={handleTransferFieldChange("rate")}
              fullWidth
              size="small"
              type="number"
            />
            <TextField
              label="Season"
              value={transferForm.season}
              onChange={handleTransferFieldChange("season")}
              fullWidth
              size="small"
              placeholder="e.g. 2025-26"
            />
            <TextField
              label="Note"
              value={transferForm.note}
              onChange={handleTransferFieldChange("note")}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!transferForm.partyName}
            onClick={async () => {
              setTransferDialogOpen(false);
              try {
                await fetch(`${API_URL}/save-SugarTransferLetter`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    Company_Code: companyCode,
                    Doc_No: doc_no,
                    Ref_No: transferForm.refNo,
                    Party_Ac_Code: transferForm.partyCode,
                    Party_Accoid: transferForm.partyAccoid,
                    Party_Name: transferForm.partyName,
                    Party_City: transferForm.partyCity,
                    Party_GST: transferForm.partyGST,
                    Grade: transferForm.grade,
                    Quantity: transferForm.quantity,
                    Rate: transferForm.rate,
                    Season: transferForm.season,
                    Note: transferForm.note,
                    Created_By: sessionStorage.getItem("username"),
                  }),
                });
              } catch (err) {
                console.error("Could not save Sugar Transfer Letter record:", err);
              }
              generateTransferLetterPdf(transferBaseData, transferForm);
            }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default LetterHeadPrintReport;